import * as THREE from 'three';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import {
  createThreeKit,
  disposeObject,
  makeGlowDisc,
  makeGridFloor,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';
import { OrbitRig } from '../shared/orbitRig';

type Cell = { c: number; r: number };
type Actor = {
  c: number;
  r: number;
  mesh: THREE.Group;
  color: number;
  x: number;
  z: number;
  tc: number;
  tr: number;
  moving: boolean;
};

/**
 * Dot Run — smooth maze chase.
 * Buffered turns, interpolated steps, gentler ghosts.
 */
export class DotRunGame implements Game {
  readonly meta: GameMeta = {
    id: 'pac',
    title: 'DOT RUN',
    blurb: 'Maze chase. Drag cam to reframe. FIRE = fright bomb.',
    tag: 'ARCADE',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private cols = 13;
  private rows = 11;
  private grid: number[][] = [];
  private pellets = new Map<string, THREE.Mesh>();
  private hero!: Actor;
  private ghosts: Actor[] = [];
  private want: { dc: number; dr: number } = { dc: 0, dr: 0 };
  private fright = 0;
  private bombCd = 0;
  private invuln = 0;
  private score = 0;
  private lives = 4;
  private wave = 1;
  private message = 'D-PAD · FIRE BOMB · DRAG CAM';
  private messageT = 2;
  private disposed = false;
  private cell = 1.45;
  private ghostTimer = 0;
  private stepDur = 0.14;
  private ghostStepDur = 0.28;

  private camTarget = new THREE.Vector3();
  private orbit = new OrbitRig({ azimuth: Math.PI / 4, elevation: 0.55, distance: 18 });

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Dot Run',
      // perspective + orbit is more fun than locked ortho
      z: 18,
      fov: 40,
      clear: 0x030a08,
      fog: 0x030a08,
      fogNear: 28,
      fogFar: 70,
    });
    this.buildLevel();
    this.orbit.attach(this.kit.renderer.domElement);
    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
  }

  private key(c: number, r: number): string {
    return `${c},${r}`;
  }

  private worldXZ(c: number, r: number): { x: number; z: number } {
    const ox = -((this.cols - 1) * this.cell) / 2;
    const oz = -((this.rows - 1) * this.cell) / 2;
    return { x: ox + c * this.cell, z: oz + r * this.cell };
  }

  private buildLevel(): void {
    while (this.kit.root.children.length) {
      const ch = this.kit.root.children[0];
      this.kit.root.remove(ch);
      disposeObject(ch);
    }
    this.pellets.clear();
    this.ghosts = [];
    this.want = { dc: 0, dr: 0 };

    // open maze — fewer pillars
    this.grid = Array.from({ length: this.rows }, (_, r) =>
      Array.from({ length: this.cols }, (_, c) => {
        if (r === 0 || c === 0 || r === this.rows - 1 || c === this.cols - 1) return 1;
        if (r % 3 === 0 && c % 3 === 0 && r > 1 && c > 1 && r < this.rows - 2 && c < this.cols - 2)
          return 1;
        return 0;
      }),
    );

    this.kit.root.add(makeGridFloor(this.cols * this.cell + 3, this.rows * this.cell + 3));

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const { x, z } = this.worldXZ(c, r);
        if (this.grid[r][c] === 1) {
          const wall = new THREE.Mesh(
            new THREE.BoxGeometry(this.cell * 0.88, 1.05, this.cell * 0.88),
            new THREE.MeshStandardMaterial({
              color: 0x164a38,
              emissive: 0x062218,
              emissiveIntensity: 0.35,
              roughness: 0.55,
              metalness: 0.15,
            }),
          );
          wall.position.set(x, 0.52, z);
          const cap = makeGlowDisc(0x3dffb0, 0.55, 0.2);
          cap.rotation.x = -Math.PI / 2;
          cap.position.set(x, 1.08, z);
          this.kit.root.add(wall, cap);
        } else {
          const pel = new THREE.Mesh(
            new THREE.SphereGeometry(0.16, 10, 10),
            new THREE.MeshStandardMaterial({
              color: 0xffe066,
              emissive: 0xaa8800,
              emissiveIntensity: 0.6,
              roughness: 0.3,
            }),
          );
          pel.position.set(x, 0.28, z);
          this.kit.root.add(pel);
          this.pellets.set(this.key(c, r), pel);
        }
      }
    }

    this.hero = this.spawnActor(1, 1, 0x00ff88, true);
    const gSpawns: [number, number, number][] = [
      [this.cols - 2, 1, 0xff4d6d],
      [1, this.rows - 2, 0x4dd4ff],
      [this.cols - 2, this.rows - 2, 0xc084ff],
    ];
    for (const [c, r, col] of gSpawns) {
      if (this.grid[r][c] === 0) this.ghosts.push(this.spawnActor(c, r, col, false));
    }
  }

  private spawnActor(c: number, r: number, color: number, isHero: boolean): Actor {
    const g = new THREE.Group();
    const body = new THREE.Mesh(
      isHero
        ? new THREE.SphereGeometry(0.42, 16, 16)
        : new THREE.SphereGeometry(0.4, 14, 14),
      new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.35,
        roughness: 0.4,
      }),
    );
    body.position.y = 0.45;
    const glow = makeGlowDisc(color, 0.85, 0.35);
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = 0.05;
    g.add(body, glow);
    const { x, z } = this.worldXZ(c, r);
    g.position.set(x, 0, z);
    this.kit.root.add(g);
    return { c, r, mesh: g, color, x, z, tc: c, tr: r, moving: false };
  }

  private can(c: number, r: number): boolean {
    if (r < 0 || c < 0 || r >= this.rows || c >= this.cols) return false;
    return this.grid[r][c] === 0;
  }

  private startMove(a: Actor, dc: number, dr: number): boolean {
    if (a.moving) return false;
    const nc = a.c + dc;
    const nr = a.r + dr;
    if (!this.can(nc, nr)) return false;
    a.tc = nc;
    a.tr = nr;
    a.moving = true;
    return true;
  }

  private tickMove(a: Actor, dt: number, dur: number): void {
    if (!a.moving) return;
    const target = this.worldXZ(a.tc, a.tr);
    const speed = this.cell / dur;
    const dx = target.x - a.x;
    const dz = target.z - a.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.02) {
      a.x = target.x;
      a.z = target.z;
      a.c = a.tc;
      a.r = a.tr;
      a.moving = false;
    } else {
      const step = Math.min(dist, speed * dt);
      a.x += (dx / dist) * step;
      a.z += (dz / dist) * step;
    }
    a.mesh.position.x = a.x;
    a.mesh.position.z = a.z;
  }

  private ghostAI(g: Actor, energy: number): void {
    if (g.moving) return;
    const opts: Cell[] = [];
    for (const [dc, dr] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      if (this.can(g.c + dc, g.r + dr)) opts.push({ c: g.c + dc, r: g.r + dr });
    }
    if (!opts.length) return;
    let pick = opts[Math.floor(Math.random() * opts.length)];
    // more random, less laser-chase
    if (this.fright > 0) {
      pick = opts.reduce((best, o) => {
        const d = Math.abs(o.c - this.hero.c) + Math.abs(o.r - this.hero.r);
        const bd = Math.abs(best.c - this.hero.c) + Math.abs(best.r - this.hero.r);
        return d > bd ? o : best;
      });
    } else if (Math.random() < 0.35 + energy * 0.15) {
      pick = opts.reduce((best, o) => {
        const d = Math.abs(o.c - this.hero.c) + Math.abs(o.r - this.hero.r);
        const bd = Math.abs(best.c - this.hero.c) + Math.abs(best.r - this.hero.r);
        return d < bd ? o : best;
      });
    }
    this.startMove(g, pick.c - g.c, pick.r - g.r);
  }

  private hit(): void {
    this.lives -= 1;
    this.invuln = 2.2;
    if (navigator.vibrate) navigator.vibrate([18, 35, 18]);
    if (this.lives <= 0) {
      this.message = 'GAME OVER — FIRE TO RETRY';
      this.messageT = 99;
    } else {
      this.hero.c = 1;
      this.hero.r = 1;
      this.hero.tc = 1;
      this.hero.tr = 1;
      this.hero.moving = false;
      const p = this.worldXZ(1, 1);
      this.hero.x = p.x;
      this.hero.z = p.z;
      this.hero.mesh.position.set(p.x, 0, p.z);
      this.message = 'HIT';
      this.messageT = 0.8;
    }
  }

  private reset(): void {
    this.score = 0;
    this.lives = 4;
    this.wave = 1;
    this.fright = 0;
    this.message = 'GO';
    this.messageT = 1.2;
    this.buildLevel();
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;
    const bands = this.ctx.getBands();
    const { energy, kick } = bands;

    if (this.messageT > 0) this.messageT -= dt;
    this.fright = Math.max(0, this.fright - dt);
    this.bombCd = Math.max(0, this.bombCd - dt);
    this.invuln = Math.max(0, this.invuln - dt);
    this.ghostTimer += dt;

    if (this.lives <= 0) {
      if (input.firePressed) this.reset();
      this.kit.renderer.render(this.kit.scene, this.kit.camera);
      return;
    }

    // buffer desired direction
    if (input.left) this.want = { dc: -1, dr: 0 };
    else if (input.right) this.want = { dc: 1, dr: 0 };
    else if (input.up) this.want = { dc: 0, dr: -1 };
    else if (input.down) this.want = { dc: 0, dr: 1 };

    this.stepDur = Math.max(0.1, 0.15 - energy * 0.02);
    // ghosts stay slower than player
    this.ghostStepDur = Math.max(0.2, 0.34 - energy * 0.08 - this.wave * 0.008);

    if (!this.hero.moving && (this.want.dc || this.want.dr)) {
      this.startMove(this.hero, this.want.dc, this.want.dr);
    }
    this.tickMove(this.hero, dt, this.stepDur);

    if (this.ghostTimer >= this.ghostStepDur * 0.85) {
      this.ghostTimer = 0;
      for (const g of this.ghosts) this.ghostAI(g, energy);
    }
    for (const g of this.ghosts) this.tickMove(g, dt, this.ghostStepDur);

    // pellets even mid-step when close to cell
    {
      const pk = this.key(this.hero.c, this.hero.r);
      // also check target cell when almost arrived
      const nearTarget =
        this.hero.moving &&
        Math.hypot(this.hero.x - this.worldXZ(this.hero.tc, this.hero.tr).x, this.hero.z - this.worldXZ(this.hero.tc, this.hero.tr).z) <
          this.cell * 0.25;
      const cellKeys = [pk];
      if (nearTarget) cellKeys.push(this.key(this.hero.tc, this.hero.tr));
      if (!this.hero.moving) cellKeys.push(pk);
      for (const k of cellKeys) {
        const pel = this.pellets.get(k);
        if (!pel) continue;
        this.kit.root.remove(pel);
        disposeObject(pel);
        this.pellets.delete(k);
        this.score += 10 + Math.floor(kick * 12);
      }
    }

    if (this.pellets.size === 0) {
      this.wave += 1;
      this.score += 200 * this.wave;
      this.message = `WAVE ${this.wave} CLEAR!`;
      this.messageT = 2.2;
      this.fright = 0;
      this.buildLevel();
    }

    if (input.firePressed && this.bombCd <= 0) {
      this.fright = 4.2 + energy * 1.2;
      this.bombCd = 14;
      this.message = 'FRIGHTENED!';
      this.messageT = 0.9;
      if (navigator.vibrate) navigator.vibrate(10);
    }

    for (const g of this.ghosts) {
      const body = g.mesh.children[0] as THREE.Mesh;
      const mat = body.material as THREE.MeshStandardMaterial;
      mat.color.setHex(this.fright > 0 ? 0x6ec6ff : g.color);
      mat.emissive.setHex(this.fright > 0 ? 0x224466 : g.color);
      if (!this.hero.moving && !g.moving && g.c === this.hero.c && g.r === this.hero.r) {
        if (this.fright > 0) {
          g.c = this.cols - 2;
          g.r = this.rows - 2;
          g.tc = g.c;
          g.tr = g.r;
          g.moving = false;
          const p = this.worldXZ(g.c, g.r);
          g.x = p.x;
          g.z = p.z;
          g.mesh.position.set(p.x, 0, p.z);
          this.score += 60;
        } else if (this.invuln <= 0) {
          this.hit();
        }
      }
    }

    this.hero.mesh.scale.setScalar(1 + kick * 0.08);
    this.hero.mesh.visible = this.invuln <= 0 || Math.floor(this.invuln * 12) % 2 === 0;
    this.camTarget.x += (this.hero.x - this.camTarget.x) * Math.min(1, dt * 5);
    this.camTarget.z += (this.hero.z - this.camTarget.z) * Math.min(1, dt * 5);
    this.camTarget.y = 0.3;
    this.orbit.apply(this.kit.camera, this.camTarget, dt);
    this.kit.renderer.render(this.kit.scene, this.kit.camera);
  }

  resize(w: number, h: number): void {
    if (!this.kit) return;
    resizeThree(this.kit, w, h, 18, 22);
  }

  getHud() {
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageT > 0 ? this.message : undefined,
    };
  }

  dispose(): void {
    this.disposed = true;
    this.orbit.detach();
    this.kit?.dispose();
  }
}
